import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, LogOut, Building2, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function adminApi(credentials) {
  return axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${credentials}`,
    },
  });
}

export default function PlatformAdmin() {
  const [credentials, setCredentials] = useState(() => localStorage.getItem('adminCredentials') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Dashboard
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Provision form
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const [provisionLoading, setProvisionLoading] = useState(false);

  const fetchTenants = useCallback(async (creds) => {
    setTenantsLoading(true);
    try {
      const res = await adminApi(creds).get('/admin/tenants');
      setTenants(res.data);
    } catch {
      toast.error('Failed to load tenants');
    } finally {
      setTenantsLoading(false);
    }
  }, []);

  // Check saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem('adminCredentials');
    if (saved) {
      adminApi(saved).get('/admin/tenants')
        .then((res) => {
          setCredentials(saved);
          setAuthenticated(true);
          setTenants(res.data);
        })
        .catch(() => {
          localStorage.removeItem('adminCredentials');
        })
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    const encoded = btoa(`${username}:${password}`);
    try {
      const res = await adminApi(encoded).get('/admin/tenants');
      localStorage.setItem('adminCredentials', encoded);
      setCredentials(encoded);
      setAuthenticated(true);
      setTenants(res.data);
      toast.success('Authenticated as platform admin');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Invalid admin credentials');
      } else {
        toast.error(err.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminCredentials');
    setCredentials('');
    setAuthenticated(false);
    setTenants([]);
    setUsername('');
    setPassword('');
  };

  const openProvisionDialog = (tenant) => {
    setSelectedTenant(tenant);
    setAdminEmail('');
    setAdminPassword('');
    setAdminName('');
    setDialogOpen(true);
  };

  const handleProvision = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setProvisionLoading(true);
    try {
      await adminApi(credentials).post('/admin/tenants/provision', {
        tenant_id: selectedTenant.tenant_id,
        admin_email: adminEmail,
        admin_password: adminPassword,
        admin_name: adminName,
      });
      toast.success(`LCA enabled for "${selectedTenant.company_name}"`);
      setDialogOpen(false);
      setSelectedTenant(null);
      setAdminEmail('');
      setAdminPassword('');
      setAdminName('');
      fetchTenants(credentials);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('LCA tables already provisioned for this tenant');
      } else {
        toast.error(err.response?.data?.detail || 'Provisioning failed');
      }
    } finally {
      setProvisionLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // ─── Login Screen ───
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 p-8">
        <div className="absolute inset-0 opacity-30 overflow-hidden">
          <div className="absolute top-20 left-20 w-72 h-72 bg-sky-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-sm relative z-10"
        >
          <div className="flex items-center gap-3 mb-8 justify-center">
            <img src="/saarv-logo.svg" alt="Saarv" className="h-16 w-auto" />
          </div>

          <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
            <CardHeader className="text-center">
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/20">
                <Shield className="h-5 w-5 text-amber-400" />
              </div>
              <CardTitle className="text-white">Platform Admin</CardTitle>
              <CardDescription className="text-slate-400">Sign in to manage tenants</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-username" className="text-slate-300">Username</Label>
                  <Input
                    id="admin-username"
                    type="text"
                    placeholder="admin username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading}>
                  {loginLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // ─── Admin Dashboard ───
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <img src="/saarv-logo.svg" alt="Saarv" className="h-8 w-auto" />
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-500" />
              <span className="font-semibold text-slate-700">Platform Admin</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Tenants</h1>
            <p className="text-sm text-slate-500">Platform tenants and LCA module status</p>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Tenant ID</TableHead>
                    <TableHead>Schema</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>LCA</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                      </TableCell>
                    </TableRow>
                  ) : tenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        No platform tenants found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tenants.map((t) => (
                      <TableRow key={t.tenant_id}>
                        <TableCell className="font-medium">{t.company_name || '\u2014'}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{t.tenant_id}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{t.schema_name}</TableCell>
                        <TableCell>
                          <Badge variant={t.status === 'active' ? 'default' : 'secondary'}>
                            {t.status || '\u2014'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {t.subscription_plan || '\u2014'}
                        </TableCell>
                        <TableCell>
                          {t.has_lca_tables ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Not provisioned</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {!t.has_lca_tables && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openProvisionDialog(t)}
                            >
                              <Building2 className="mr-1 h-3 w-3" />
                              Provision LCA
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Provision LCA Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Enable LCA for {selectedTenant?.company_name}</DialogTitle>
                <DialogDescription>
                  Provision LCA tables and create an admin user for this tenant.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleProvision} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="prov-email">Admin Email</Label>
                  <Input
                    id="prov-email"
                    type="email"
                    placeholder="admin@company.com"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prov-password">Admin Password</Label>
                  <Input
                    id="prov-password"
                    type="password"
                    placeholder="Min 8 characters"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prov-name">Admin Name</Label>
                  <Input
                    id="prov-name"
                    placeholder="Jane Doe"
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    required
                    minLength={1}
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={provisionLoading}>
                    {provisionLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Provisioning...</>
                    ) : (
                      <><Building2 className="mr-2 h-4 w-4" /> Provision LCA</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>
      </main>
    </div>
  );
}
